import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import AuthLayout from '../components/auth/AuthLayout';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);

    // Fake network call
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 2000);
  };

  return (
    <AuthLayout>

          <div className="mb-6 text-center lg:text-left">
            <Link to="/login" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-brand-black transition-colors mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>

            {submitted ? (
              <div className="mb-8">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6 mx-auto lg:mx-0">
                  <MailCheck className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Check Your Email</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We've sent a password reset link to <span className="text-brand-black font-bold">{email}</span>.
                  Please check your inbox and follow the instructions.
                </p>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Forgot Password?</h2>
                <p className="text-gray-500 text-sm">No worries, it happens to the best of us. Enter your email to reset your secret key.</p>
              </div>
            )}
          </div>

          {!submitted && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-brand-black uppercase tracking-wide mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="name@company.com"
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-orange-500 text-white py-3 rounded-xl font-bold hover:bg-brand-orange-600 transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? 'Sending Link...' : 'Send Reset Link'}
              </button>

              <div className="bg-brand-orange-50/50 rounded-xl p-4 border border-brand-orange-100/50 mt-8">
                 <p className="text-[11px] text-brand-black leading-relaxed font-medium">
                   <span className="font-black uppercase tracking-wider block mb-1">Demo Mode</span>
                   Clicking the button will simulate sending a reset link. In a real environment, you'd receive an email shortly.
                 </p>
                 <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-[10px] font-black uppercase text-brand-black hover:text-brand-black mt-2 tracking-widest inline-flex items-center gap-1"
                 >
                   Jump to Reset Page (DEV ONLY)
                   <ChevronRight className="w-3 h-3" />
                 </button>
              </div>
            </form>
          )}

          {submitted && (
            <button
              onClick={() => setSubmitted(false)}
              className="w-full bg-white border border-gray-200 text-brand-black py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors shadow-sm mt-6"
            >
              Try another email
            </button>
          )}
    </AuthLayout>
  );
};

// Internal component for the jump link icon
const ChevronRight = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

export default ForgotPassword;
